import api from './api';

export interface TraineeTraining {
  id: number;
  title: string;
  description: string;
  center: {
    id: number;
    name: string;
    address: string;
  };
  status: 'enrolled' | 'completed' | 'cancelled';
  progress: number;
  startDate: string;
  endDate: string;
  nextSession?: string;
  totalSessions: number;
  completedSessions: number;
  certificate?: string;
  image?: string;
  price: number;
  duration: string;
  level: string;
  domaine: string;
}

export interface TraineeProfile {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  createdAt: string;
  stats: {
    enrolledCourses: number;
    completedCourses: number;
    certificates: number;
    totalHours: number;
    averageScore: number;
    upcomingSessions: number;
  };
}

export interface TrainingSession {
  id: number;
  trainingId: number;
  title: string;
  description: string;
  center: {
    id: number;
    name: string;
    address: string;
  };
  field: string;
  sessionDate: string;
  duration: string;
  maxParticipants: number;
  currentParticipants: number;
  price: number;
  format: string;
  location: string;
  availableSpots: number;
}

export interface TrainingWithSessions {
  id: string;
  title: string;
  description: string;
  center: {
    id: number;
    name: string;
    address: string;
  };
  field: string;
  sessions: Session[];
  isSubscribed: boolean;
}

export interface Session {
  id: number;
  trainingId: number;
  sessionDate: string;
  duration: string;
  maxParticipants: number;
  currentParticipants: number;
  price: number;
  format: string;
  location: string;
  availableSpots: number;
  isSubscribed: boolean;
}

export interface Subscription {
  id: number;
  session: {
    id: number;
    title: string;
    sessionDate: string;
    duration: string;
    location: string;
    format: string;
  };
  center: {
    id: number;
    name: string;
    address: string;
  };
  subscriptionDate: string;
  status: string;
}

class TraineeService {
  // Get trainee's enrolled trainings
  async getMyTrainings(): Promise<TraineeTraining[]> {
    try {
      const response = await api.get('/trainee/trainings');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get trainee profile from trainee table
  async getProfile(): Promise<TraineeProfile> {
    try {
      // First try to get from API
      const response = await api.get('/trainee/profile');
      return response.data;
    } catch (error) {
      // Fallback to user data from localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        
        // Extract trainee data if available
        const traineeData = user.trainee || {};
        
        return {
          id: user.id || 1,
          firstname: traineeData.firstname || user.firstName || 'Utilisateur',
          lastname: traineeData.lastname || user.lastName || 'PHINLEX',
          email: traineeData.email || user.email || 'user@example.com',
          phone: traineeData.phone || user.phone || '',
          createdAt: user.createdAt || '2023-01-15',
          stats: {
            enrolledCourses: 3,
            completedCourses: 1,
            certificates: 1,
            totalHours: 156,
            averageScore: 85,
            upcomingSessions: 2
          }
        };
      }
      
      // Default fallback
      return {
        id: 1,
        firstname: 'Utilisateur',
        lastname: 'PHINLEX',
        email: 'user@example.com',
        phone: '',
        createdAt: '2023-01-15',
        stats: {
          enrolledCourses: 0,
          completedCourses: 0,
          certificates: 0,
          totalHours: 0,
          averageScore: 0,
          upcomingSessions: 0
        }
      };
    }
  }

  // Update trainee profile
  async updateProfile(profileData: Partial<TraineeProfile>): Promise<TraineeProfile> {
    try {
      const response = await api.patch('/trainee/profile', profileData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get available training sessions
  async getAvailableSessions(): Promise<TrainingSession[]> {
    try {
      const response = await api.get('/trainee/sessions/available');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get training details with sessions
  async getTrainingSessions(trainingId: number): Promise<TrainingWithSessions> {
    try {
      const response = await api.get(`/trainee/trainings/${trainingId}/sessions`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Subscribe to a training session
  async subscribeToSession(sessionId: number, subscriptionData?: any): Promise<any> {
    try {
      const response = await api.post(`/trainee/sessions/${sessionId}/subscribe`, subscriptionData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Change session subscription
  async changeSessionSubscription(sessionId: number): Promise<any> {
    try {
      const response = await api.post(`/trainee/sessions/${sessionId}/change-subscription`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get user's subscriptions
  async getUserSubscriptions(): Promise<Subscription[]> {
    try {
      const response = await api.get('/trainee/subscriptions');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId: number): Promise<any> {
    try {
      const response = await api.post(`/trainee/subscriptions/${subscriptionId}/cancel`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get training details
  async getTrainingDetails(trainingId: number): Promise<TraineeTraining> {
    try {
      const response = await api.get(`/trainee/trainings/${trainingId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get user's subscription information for form pre-filling
  async getSubscriptionInfo(): Promise<any> {
    try {
      const response = await api.get('/trainee/subscription-info');
      return response.data;
    } catch (error) {
      // Return empty form data if no subscription info found
      return {
        current_position: '',
        company_name: '',
        department: '',
        years_of_experience: '',
        has_special_accommodation: false,
        special_accommodation_details: ''
      };
    }
  }

  // Download certificate
  async downloadCertificate(trainingId: number): Promise<Blob> {
    try {
      const response = await api.get(`/trainee/trainings/${trainingId}/certificate`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Delete account permanently
  async deleteAccount(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete('/trainee/account');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default new TraineeService();
